import type { UserTableViewOption, ViewOptionId } from '@/types';
import axios from 'axios';
import { defineStore } from 'pinia';

const TABLE_IDENTIFIER = 'employee-dashboard';

// The default view is virtual: it has no database row, can't be edited or
// deleted, and represents the dashboard's built-in column showing. Its display
// name is resolved from the locale in the components.
export const DEFAULT_VIEW_OPTION: UserTableViewOption = {
    id: 'default',
    name: '',
    table_identifier: TABLE_IDENTIFIER,
    is_active: false,
    is_global: true,
    is_owner: false,
    is_default: true,
    column_visibility: {},
    column_order: null,
    default_editing: false,
};

export type ViewOptionsState = {
    viewOptions: UserTableViewOption[];
    activeId: ViewOptionId;
    state: 'cancel' | 'submit' | 'edit';
};

export const useViewOptionsStore = defineStore('view-options', {
    state: (): ViewOptionsState => ({
        viewOptions: [],
        activeId: 'default',
        state: 'cancel',
    }),
    getters: {
        allViewOptions: (state): UserTableViewOption[] => [
            DEFAULT_VIEW_OPTION,
            ...state.viewOptions,
        ],
        // Default plus global views from other users. Your own shared views stay
        // in the personal list so you can still manage them there.
        globalViewOptions: (state): UserTableViewOption[] => [
            DEFAULT_VIEW_OPTION,
            ...state.viewOptions.filter((option) => option.is_global && !option.is_owner),
        ],
        personalViewOptions: (state): UserTableViewOption[] =>
            state.viewOptions.filter((option) => option.is_owner),
        activeViewOption(state): UserTableViewOption {
            return (
                this.allViewOptions.find((option) => option.id === state.activeId) ??
                DEFAULT_VIEW_OPTION
            );
        },
        editState: (state) => state.state === 'edit',
    },
    actions: {
        initializeViewOptions(
            viewOptions: UserTableViewOption[],
            activeViewOptionId: number | null,
        ) {
            this.viewOptions = viewOptions;
            this.activeId = activeViewOptionId ?? 'default';
        },
        setActive(viewOption: UserTableViewOption) {
            this.activeId = viewOption.id;

            return axios.post(route('v2.dashboard.view-options.set-active'), {
                table_identifier: TABLE_IDENTIFIER,
                view_option_id: viewOption.is_default ? null : viewOption.id,
            });
        },
        addViewOption(viewOption: UserTableViewOption) {
            this.viewOptions.push({ ...viewOption, default_editing: true });
            this.setActive(viewOption);
        },
        updateViewOption(viewOptionId: ViewOptionId, name: string) {
            this.viewOptions = this.viewOptions.map((option) =>
                option.id === viewOptionId ? { ...option, name } : option,
            );
        },
        toggleGlobal(viewOption: UserTableViewOption) {
            const isGlobal = !viewOption.is_global;

            this.viewOptions = this.viewOptions.map((option) =>
                option.id === viewOption.id ? { ...option, is_global: isGlobal } : option,
            );

            return axios.patch(route('v2.dashboard.view-options.update', viewOption.id), {
                is_global: isGlobal,
            });
        },
        deleteViewOption(viewOptionId: ViewOptionId) {
            if (this.activeId === viewOptionId) {
                this.setActive(DEFAULT_VIEW_OPTION);
            }

            this.viewOptions = this.viewOptions.filter((option) => option.id !== viewOptionId);
        },
        removeDefaultEditing(viewOptionId: ViewOptionId) {
            this.viewOptions = this.viewOptions.map((option) =>
                option.id === viewOptionId ? { ...option, default_editing: false } : option,
            );
        },
        updateColumnVisibility(
            viewOptionId: ViewOptionId,
            columnVisibility: Record<string, boolean>,
        ) {
            this.viewOptions = this.viewOptions.map((option) =>
                option.id === viewOptionId
                    ? { ...option, column_visibility: columnVisibility }
                    : option,
            );

            if (viewOptionId === 'default') {
                return;
            }

            return axios.patch(route('v2.dashboard.view-options.update', viewOptionId), {
                column_visibility: columnVisibility,
            });
        },
        updateColumnOrder(viewOptionId: ViewOptionId, columnOrder: string[]) {
            this.viewOptions = this.viewOptions.map((option) =>
                option.id === viewOptionId
                    ? { ...option, column_order: columnOrder }
                    : option,
            );

            if (viewOptionId === 'default') {
                return;
            }

            return axios.patch(route('v2.dashboard.view-options.update', viewOptionId), {
                column_order: columnOrder,
            });
        },
        setState(state: 'cancel' | 'submit' | 'edit') {
            this.state = state;
        },
    },
});
